import { Box, Flex, Image, Td, Tooltip, Tr } from '@chakra-ui/react';
import { BigRational } from 'big-rational-ts';
import BigNumber from 'bignumber.js';
import { Balance, assetToUnit } from '../blockchain/client';
import { formatState } from '../blockchain/client/interact';
import StatusDot from './atoms/svg/statusDot-svg';
import { stringToFormattedNumber } from './stringToNumber';
import { ArrayElement, IPortfolioState } from '../types';
import { bigRationalToBigNumber } from '../utils';
import { themeVariables } from '../theme';

const abs = (n: bigint) => (n < 0n ? -n : n);

function PortfolioItem(props: {
  item: ArrayElement<IPortfolioState['assets']>;
  balance: Balance | null;
  type: 'mint' | 'burn';
  amountsToModify: Record<string, bigint>;
  formattedState: ReturnType<typeof formatState>;
  fontSize?: string;
}) {
  const brZero = new BigRational(0n, 1n);
  const { fontSize = '12px' } = props;
  // Portfolio metrics before tx
  const adaPortfolioWorth = Object.entries(
    props.formattedState.state.assets,
  ).reduce(
    (acc, [asset, amount]) => {
      const assetWorth = new BigRational(amount, 1n).mul(
        props.formattedState.prices[asset],
      );
      return acc.add(assetWorth).reduce();
    },
    new BigRational(0n, 1n),
  );

  const adaWorthInPortfolio = props.formattedState.prices[props.item.asset.id]
    .mul(
      new BigRational(
        props.formattedState.state.assets[props.item.asset.id],
        1n,
      ),
    )
    .reduce();
  const tokensInPortfolio =
    props.formattedState.state.assets[props.item.asset.id];

  const worthPercentageInPortfolio = adaWorthInPortfolio
    .div(adaPortfolioWorth)
    .mul(new BigRational(100n, 1n).reduce());

  // Tx metrics
  const adaTxWorth = Object.entries(props.amountsToModify).reduce(
    (acc, [asset, amount]) => {
      const assetWorth = new BigRational(amount, 1n).mul(
        props.formattedState.prices[asset],
      );
      return acc.add(assetWorth).reduce();
    },
    new BigRational(0n, 1n),
  );
  const adaWorthInTx = props.formattedState.prices[props.item.asset.id]
    .mul(new BigRational(props.amountsToModify[props.item.asset.id], 1n))
    .reduce();
  const worthPercentageInTx = adaTxWorth.eq(brZero)
    ? brZero
    : adaWorthInTx.div(adaTxWorth).mul(new BigRational(100n, 1n)).reduce();
  const tokensInTx = props.amountsToModify[props.item.asset.id];

  // Portfolio metrics after tx
  const worthOfPortfolioAfterTx = adaTxWorth.add(adaPortfolioWorth).reduce();
  const adaWorthAfterTx = adaWorthInPortfolio.add(adaWorthInTx).reduce();
  const worthPercentageAfterTx = worthOfPortfolioAfterTx.eq(brZero)
    ? brZero
    : adaWorthAfterTx
        .div(worthOfPortfolioAfterTx)
        .mul(new BigRational(100n, 1n))
        .reduce();
  const tokensAfterTx = tokensInPortfolio + tokensInTx;

  const amountToModify = abs(
    props.amountsToModify[props.item.asset.id] || 0n,
  ).toString();

  const hasEnoughToken = BigNumber(
    (props.amountsToModify[props.item.asset.id] || 0n).toString(),
  ).lte(
    BigNumber(
      (props.balance?.[assetToUnit(props.item.asset)] || 0n).toString(),
    ),
  );

  return (
    <Tr verticalAlign="middle" fontSize={fontSize}>
      <Td
        textAlign="left"
        align="center"
        flexDir={'row'}
        paddingX={'6px'}
        paddingY={'12px'}
        paddingLeft={'10px'}
        width={'fit-content'}
      >
        <Flex justify="left">
          <Flex justify="left">
            <Flex flexDir="row" gap={2} align="center">
              <Box
                width="1.75rem"
                height="1.75rem"
                overflow="hidden"
                borderRadius="full"
                background={themeVariables.purple.metera200}
              >
                <Image
                  boxSize="100%"
                  objectFit="cover"
                  src={props.item.asset.imageUrl}
                  alt={props.item.asset.ticker}
                />
                -
              </Box>
              <Box
                fontSize="0.785rem"
                color={themeVariables.primary.primary50}
                fontWeight="600"
              >
                {props.item.asset.ticker}
              </Box>
            </Flex>
          </Flex>
        </Flex>
      </Td>
      <Td
        align="center"
        fontSize={fontSize === '12px' ? '1rem' : fontSize}
        paddingX={'6px'}
        paddingY={'12px'}
      >
        <Flex gap={2} justify="flex-end">
          <PortfolioDataItem
            color={themeVariables.primary.primary50}
            value={BigNumber(amountToModify.toString())}
            decimals={props.item.asset.decimals}
          />
        </Flex>
      </Td>
      <Td
        align="center"
        fontSize={fontSize === '12px' ? '1rem' : fontSize}
        paddingX={'6px'}
        paddingY={'12px'}
      >
        <Flex gap={1} justify="right">
          <Box>₳</Box>
          <PortfolioDataItem
            color={themeVariables.primary.primary50}
            value={bigRationalToBigNumber(adaWorthInTx).abs()}
            decimals={0}
          />
        </Flex>
      </Td>

      {props.type === 'mint' && (
        <Td
          align="center"
          fontSize={fontSize === '12px' ? '1rem' : fontSize}
          paddingX={'6px'}
          paddingY={'12px'}
        >
          <Flex align="center" justifyContent={'flex-end'} gap={2}>
            {hasEnoughToken ? (
              <StatusDot showStatus={false} status="Confirmed" />
            ) : (
              <Tooltip
                sx={{
                  background: themeVariables.cards.background,
                  border: `0.5px solid `,
                  borderRadius: '1rem',
                  color: themeVariables.offWhite.offWhite400,
                  fontWeight: '600',
                  padding: '1rem',
                  backdropFilter: 'blur(30px)',
                }}
                label={`You don't have enough ${props.item.asset.name}. Click the dot to visit our faucet.`}
              >
                <button>
                  <a href="/faucet" style={{ color: 'var(--link-color)' }}>
                    <StatusDot showStatus={false} status="Cancelled" />
                  </a>
                </button>
              </Tooltip>
            )}
            <PortfolioDataItem
              value={BigNumber(
                (
                  props.balance?.[assetToUnit(props.item.asset)] || 0n
                ).toString(),
              )}
              decimals={props.item.asset.decimals}
              color={themeVariables.primary.primary50}
            />
          </Flex>
        </Td>
      )}
    </Tr>
  );
}

const PortfolioDataItem = (props: {
  title?: string;
  value: BigNumber;
  color?: string;
  decimals: number;
}) => (
  <Flex flexDir={'column'} whiteSpace="nowrap">
    <Box color={props.color}>
      {stringToFormattedNumber(
        props.value.div(10 ** props.decimals).toString(),
      )}
    </Box>
  </Flex>
);

export default PortfolioItem;
